import { P2TextDescription } from 'react-bootstrap-common-script'
import { Separator } from '../../../components/separator'
import { LoanTypeEnum } from '../../../models/enum/loan-type.enum'
import CurrentLoadSegment from '../segments/current-loan-segment'
import FinancingForm from '../../../components/HOC/financing-form/financing-form'
import { StageBConveyancing, StageBPersonalDetail } from '../../../models/classes/stage-b/review-endorse.class'
import { useAppSelector } from '../../../redux/hook'
import { ApplicationStep2 } from '../../../models/classes/ApplicationStep2.class'
import { PolymerAxios } from '../../../utils/axios';
import { API_HLEStatus } from '../../../utils/api';
import { useCallback, useEffect, useMemo, useState } from 'react'
import { SolicitorTypeEnum } from '../../../models/enum/solicitor-type.enum'
import { LegalRepresentation } from '../../../models/classes/ApplicationStep3.class'
import { PolymerCrypto } from '../../../utils/encryptor'
import { checkIsFlatResale } from '../../../utils/form-helper'

interface ReviewFinancingProps {
  financial_values: ApplicationStep2;
  conveyancing_values: StageBConveyancing;
  isLoginOutgoing: boolean;
  isMainOutgoing: boolean;
  isMainApplicantCreateApplication?: boolean;
  setFormValue: React.Dispatch<React.SetStateAction<ApplicationStep2>>;
  setConveyancingValue: React.Dispatch<React.SetStateAction<StageBConveyancing>>;
  setHLeApprovalPass?: (value: boolean | undefined) => void;

  forceError?: boolean;
}
function ReviewFinancing({...props }: ReviewFinancingProps) {
  const applicationStartFormData = useAppSelector((state) => state.app.ApplicationStartSlice.formData);
  const applicationStep1FormData = useAppSelector((state) => state.app.ApplicationStep1Slice.formData);
  const applicationStep2FormData = useAppSelector((state) => state.app.ApplicationStep2Slice.formData);
  const loginUser = useAppSelector(state => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);

  const [alertNumber, setAlertNumber] = useState<number>(0);
  
  const isFlatResale = useMemo(()=>{
    return checkIsFlatResale(applicationStartFormData.selected_hdb_property?.financing_info.sales_type_code!); 
  },[applicationStartFormData.selected_hdb_property?.financing_info.sales_type_code]);

  const newLoanType = useMemo(() => props.financial_values.new_loan_type, [props.financial_values]);

  const fetchHleResponse = async () => {
    console.log("DEBUG -- CALL HLE");
    try{
      const proposedOwners = applicationStep1FormData.household.incoming_owners.map(item => {
        return {
          uinfin: PolymerCrypto.encryption(item.myinfo.uinfin),
        }
      });

      const proposedOccupiers = applicationStep1FormData.household.incoming_occupiers.map((item:any) => {
        return {
          uinfin: PolymerCrypto.encryption(item.myinfo.uinfin),
        }
      });
      
      const response = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(`${API_HLEStatus}`, {
        uinfin: PolymerCrypto.encryption(loginUser?.uinfin) ?? "",
        owners: proposedOwners,
        occupiers: proposedOccupiers
      });
      
      if (response.data.hle_check_status === "UNAVAILABLE") {
        console.log("STEP B - HLE check unavailable (BR21/HHA)");
        setAlertNumber(4);
        props.setHLeApprovalPass?.(false);
      } else if (response.data.hle_check_status === "DUPLICATE") {
        console.log("STEP B - HLE duplicate cases (BR21/HHA)");
        setAlertNumber(5);
        props.setHLeApprovalPass?.(false);
      } else if (response.data.valid_hle === false) {
        console.log("STEP B - hle status return false");
        setAlertNumber(2);
        props.setHLeApprovalPass?.(false);
      } else {
        // Household match is already enforced by Polymer checkHle / HhaService.
        // Response no longer returns hle_proposed_owners (same as Stage A2).
        setAlertNumber(1);
        props.setHLeApprovalPass?.(true);
      }
    } catch (error) {
      console.log(error);
      setAlertNumber(4);
      props.setHLeApprovalPass?.(false);
    }
  };

  useEffect(()=>{
    setAlertNumber(0);

    if (props.isLoginOutgoing || newLoanType !== LoanTypeEnum.hdb_loan) {
      props.setHLeApprovalPass?.(true)
    } else if (props.isMainOutgoing) {
      fetchHleResponse();
    }
  },[newLoanType])

  useEffect(() => {
    //If its not outgoing owner, there is no need to check for HLE because the outgoing owner unable to select the proposed owner new loan.
    if(!props.isLoginOutgoing){
      props.setHLeApprovalPass?.(true); 
    }
  },[props.isLoginOutgoing])

  const handleOnCurrencyChange = useCallback((amount:string)=>{
    props.setFormValue({...props.financial_values, current_loan_amount:amount})
  },[props])

  const handleOnLoanTypeChange = useCallback((value:string)=>{
    if (props.financial_values.new_loan_type !== LoanTypeEnum.bank_loan && (value as LoanTypeEnum) === LoanTypeEnum.bank_loan && props.conveyancing_values.proposed_solicitor_type === SolicitorTypeEnum.hdb_solicitor) {
      props.setConveyancingValue({...props.conveyancing_values, proposed: new LegalRepresentation(), proposed_same_solicitor: false, proposed_solicitor_type: undefined})
      //resetting legal representation if bank loan is selected and previous selection for solicitor is hdb, as hdb is not allowed for bank loan
    }

    props.setFormValue({...props.financial_values, new_loan_type: value as LoanTypeEnum})
  },[props])

  const handleMultipleFinancialValueChange = useCallback((newValues: Partial<ApplicationStep2>) => {
    props.setFormValue({...props.financial_values, ...newValues})
  }, [props.financial_values, props.setFormValue])

  const handleOnLoanAmountChange = useCallback((value: string)=>{
    props.setFormValue({...props.financial_values, new_loan_amount: value})
  },[props])

  return (
    <div className='px-4 pt-4 pb-5'>
      <P2TextDescription color='#000' title='Financing' />
    
      <div className='mt-4'>
        <CurrentLoadSegment isDisabled={true} loan_type={applicationStep2FormData.current_loan_type} loan_outstanding_amount={props.financial_values.current_loan_amount} 
        setOnCurrencyChange={handleOnCurrencyChange} />
      </div>
      
      {!props.isLoginOutgoing && (
        <>
          <Separator style={{ margin: "32px 0" }} />
          <FinancingForm
            isFlatResale={isFlatResale}
            show_new_loan_info={(!props.isLoginOutgoing && !props.isMainOutgoing) || props.isMainApplicantCreateApplication}
            flat_type={applicationStartFormData.selected_hdb_property?.flat_type}
            change_ownership_reason={applicationStep1FormData.reason.change_ownership_reason}
            has_outgoing_owner={false}
            current_loan_type={applicationStep2FormData.current_loan_type}
            applicationStep2FormData={props.financial_values}
            onNewLoanTypeChange={handleOnLoanTypeChange}
            onNewLoanAmountChange={handleOnLoanAmountChange}
            handleMultipleFinancialValueChange={handleMultipleFinancialValueChange}
            alertNumber={alertNumber}
            forceError={props.forceError}
          />
        </>
      )}
    </div>

  )
}

export default ReviewFinancing